// Single source of truth for the friendly names + display order of the
// public-records systems we check per person.
//
// Why this lives in lib/ and not inside BackgroundChecksStatus or
// ExternalSourcesPanel: both of those components rendered their own
// label maps in parallel, plus PersonPage rendered a third one in
// `pendingSourceLabels`. Renaming "NJ Prison" → "NJ DOC" or reordering
// would have required editing all three. Now there's one map.
//
// Keep these labels in sync with the server-side names emitted by the
// scrape pipeline (jobs/external metadata), and with the user-facing
// copy in Today's "Still gathering data" banner.

export type ExternalSourceKey =
  | "pgpa"
  | "jocpa"
  | "doc"
  | "mvc-surcharge"
  | "mvc-restoration";

/** MCCS / municipal court is the main scrape - not an external_lookup
 *  row - but BackgroundChecksStatus renders it alongside externals.
 *  Components that mix MPA in extend ExternalSourceKey | "mpa". */
export type SourceKey = ExternalSourceKey | "mpa";

/** Profile fields a scrape needs in order to even attempt the search.
 *  Used by the per-source action button to swap "Re-scrape" with a
 *  helpful "Add DL to scrape" prompt when the field is still missing
 *  on the participant's profile. Fields the scraper would populate
 *  itself (e.g. address) don't go here - only inputs the scraper
 *  needs to construct its query. */
export type RequiredField = "license_number" | "ssn" | "zip_code";

export const FIELD_LABELS: Record<RequiredField, string> = {
  license_number: "driver's license number",
  ssn: "Social Security number",
  zip_code: "5-digit ZIP code",
};

export const FIELD_LABELS_SHORT: Record<RequiredField, string> = {
  license_number: "DL",
  ssn: "SSN",
  zip_code: "ZIP",
};

interface SourceMeta {
  key: SourceKey;
  /** User-facing name. Plain English, not internal jargon. */
  label: string;
  /** Hover tooltip - explains what this source is in 1-2 sentences. */
  tooltip: string;
  /** True for external_lookups sources; false for MPA (job-level scrape). */
  external: boolean;
  /** Profile fields this source needs to even attempt a search.
   *  Empty if the scrape works off name + DOB alone. */
  requires?: ReadonlyArray<RequiredField>;
}

/** Sources turned off entirely - hidden from BackgroundChecksStatus and
 *  the per-person External Sources panel. Mirrors the server-side
 *  DEACTIVATED_SOURCES set in server/lib/sourceCooldown.ts; keep in
 *  sync when flipping a source on or off.
 *
 *  Currently disabled:
 *   • "doc" - NJDOC's WAF reputation-flags our home IP within a few
 *     requests. Re-enable once a residential proxy is in place. */
export const DEACTIVATED_SOURCES = new Set<SourceKey>(["doc"]);

/** Display order: most-impactful first, MVC last. MPA leads because it's
 *  the headline "did the main scrape finish" check; everything below it
 *  augments. */
const ALL_SOURCES_RAW: SourceMeta[] = [
  { key: "mpa",             label: "Municipal Court",  external: false, tooltip: "Municipal-court cases statewide - traffic, parking, low-grade crim." },
  { key: "pgpa",            label: "Superior Court",   external: true,  tooltip: "Indictable / felony cases statewide." },
  { key: "jocpa",           label: "Court Judgments",  external: true,  tooltip: "Filed conviction / acquittal / dismissal judgments (June 2017+)." },
  { key: "doc",             label: "NJ Prison",        external: true,  tooltip: "NJ Department of Corrections inmate records." },
  { key: "mvc-surcharge",   label: "MVC Surcharges",   external: true,  tooltip: "Outstanding NJ surcharge balance (DWI, points, etc.).", requires: ["license_number"] },
  { key: "mvc-restoration", label: "MVC Restoration",  external: true,  tooltip: "Outstanding NJ MVC restoration fees on a suspended license.", requires: ["license_number"] },
];

export const ALL_SOURCES: SourceMeta[] = ALL_SOURCES_RAW.filter(
  (s) => !DEACTIVATED_SOURCES.has(s.key),
);

/** Just the external_lookups subset (pgpa, jocpa, doc, mvc-*). */
export const EXTERNAL_SOURCES: ReadonlyArray<SourceMeta> = ALL_SOURCES.filter((s) => s.external);

/** Lookup helper: friendly label for any source key. Returns the key
 *  itself as a fallback so we never render `undefined`. */
export function sourceLabel(key: SourceKey | string): string {
  return ALL_SOURCES.find((s) => s.key === key)?.label ?? String(key);
}

/** Profile fields a source needs that AREN'T present on the participant.
 *  Pass the participant's profile values; we return the requires[] minus
 *  whatever has been filled in. Empty array = source is ready to scrape. */
export function missingRequiredFields(
  key: SourceKey | string,
  profile: { license_number?: string | null; ssn?: string | null; zip_code?: string | null },
): RequiredField[] {
  const meta = ALL_SOURCES.find((s) => s.key === key);
  const requires = meta?.requires ?? [];
  return requires.filter((f) => {
    const v = (profile as Record<RequiredField, string | null | undefined>)[f];
    return !v || !String(v).trim();
  });
}

/** Display order for external_lookups, used by PersonPage's pending-source
 *  banner so the labels in "Still waiting on: X, Y, Z" appear in the same
 *  order as the BackgroundChecksStatus rows below the fold. */
export const EXTERNAL_SOURCE_ORDER: ReadonlyArray<ExternalSourceKey> = EXTERNAL_SOURCES.map(
  (s) => s.key as ExternalSourceKey,
);
